/**
 * Apprendre à lire — module pédagogique de lecture du Coran.
 *
 *   /cours/lecture
 *
 * Modules progressifs : alphabet 28 lettres · harakāt · sukūn · tanwīn ·
 * shadda · voyelles longues. Données statiques côté client (pas d'audio
 * en V1 ; à étendre).
 */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BookOpenText, ChevronRight, Play, Pause } from 'lucide-react';
import { GlassCard, SectionHeader } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { usePreferences } from '../context/PreferencesContext';
import { ExercisesHub } from '../components/courses/ExercisesHub';
import { SkillResultsPanel } from '../components/courses/SkillResultsPanel';
import { CurriculumJourney } from '../components/courses/CurriculumJourney';
import { JourneyCompetencyRunner } from '../components/courses/JourneyCompetencyRunner';

// Resolve the API base URL from CRA env to build absolute audio URLs.
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const audioUrl = (voice, id) =>
  `${API_BASE}/api/lecture/audio/${voice}/${id}.mp3`;

/**
 * Single shared <audio> element controlled by a tiny pub/sub mechanism so
 * playing one card automatically pauses any other.
 */
const usePlayer = (voice) => {
  const ref = useRef(null);
  const [playingId, setPlayingId] = useState(null);
  const play = (id) => {
    if (!ref.current) ref.current = new Audio();
    if (playingId === id) {
      ref.current.pause();
      setPlayingId(null);
      return;
    }
    ref.current.pause();
    ref.current.src = audioUrl(voice, id);
    ref.current.onended = () => setPlayingId(null);
    ref.current.onerror = () => setPlayingId(null);
    ref.current.play().catch(() => setPlayingId(null));
    setPlayingId(id);
  };
  return { play, playingId };
};

const PlayButton = ({ id, playingId, onClick, label = 'Écouter' }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick(id); }}
    aria-label={label}
    title={label}
    className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 border border-primary/30 text-primary hover:bg-primary/25 transition-colors"
    data-testid={`lecture-audio-play-${id}`}
  >
    {playingId === id
      ? <Pause className="w-3.5 h-3.5" />
      : <Play className="w-3.5 h-3.5 ml-0.5" />}
  </button>
);

// 28 letters of the Arabic alphabet with their 4 positional forms.
// Tuple = [letter, name, isolated, finalForm, medial, initial, translit, audioId]
const ALPHABET = [
  ['ا', 'alif',   '—',   'ـا',  'ا',   'ا',   'ʾ / ā',  'alif'],
  ['ب', 'bāʾ',    'ب',   'ـب',  'ـبـ', 'بـ',  'b',      'baa'],
  ['ت', 'tāʾ',    'ت',   'ـت',  'ـتـ', 'تـ',  't',      'taa'],
  ['ث', 'thāʾ',   'ث',   'ـث',  'ـثـ', 'ثـ',  'th',     'thaa'],
  ['ج', 'jīm',    'ج',   'ـج',  'ـجـ', 'جـ',  'j',      'jim'],
  ['ح', 'ḥāʾ',    'ح',   'ـح',  'ـحـ', 'حـ',  'ḥ',      'haa1'],
  ['خ', 'khāʾ',   'خ',   'ـخ',  'ـخـ', 'خـ',  'kh',     'khaa'],
  ['د', 'dāl',    'د',   'ـد',  'ـد',  'د',   'd',      'dal'],
  ['ذ', 'dhāl',   'ذ',   'ـذ',  'ـذ',  'ذ',   'dh',     'dhal'],
  ['ر', 'rāʾ',    'ر',   'ـر',  'ـر',  'ر',   'r',      'raa'],
  ['ز', 'zāy',    'ز',   'ـز',  'ـز',  'ز',   'z',      'zay'],
  ['س', 'sīn',    'س',   'ـس',  'ـسـ', 'سـ',  's',      'sin'],
  ['ش', 'shīn',   'ش',   'ـش',  'ـشـ', 'شـ',  'sh',     'shin'],
  ['ص', 'ṣād',    'ص',   'ـص',  'ـصـ', 'صـ',  'ṣ',      'sad'],
  ['ض', 'ḍād',    'ض',   'ـض',  'ـضـ', 'ضـ',  'ḍ',      'dad'],
  ['ط', 'ṭāʾ',    'ط',   'ـط',  'ـطـ', 'طـ',  'ṭ',      'taa2'],
  ['ظ', 'ẓāʾ',    'ظ',   'ـظ',  'ـظـ', 'ظـ',  'ẓ',      'dhaa'],
  ['ع', 'ʿayn',   'ع',   'ـع',  'ـعـ', 'عـ',  'ʿ',      'ayn'],
  ['غ', 'ghayn',  'غ',   'ـغ',  'ـغـ', 'غـ',  'gh',     'ghayn'],
  ['ف', 'fāʾ',    'ف',   'ـف',  'ـفـ', 'فـ',  'f',      'faa'],
  ['ق', 'qāf',    'ق',   'ـق',  'ـقـ', 'قـ',  'q',      'qaf'],
  ['ك', 'kāf',    'ك',   'ـك',  'ـكـ', 'كـ',  'k',      'kaf'],
  ['ل', 'lām',    'ل',   'ـل',  'ـلـ', 'لـ',  'l',      'lam'],
  ['م', 'mīm',    'م',   'ـم',  'ـمـ', 'مـ',  'm',      'mim'],
  ['ن', 'nūn',    'ن',   'ـن',  'ـنـ', 'نـ',  'n',      'nun'],
  ['ه', 'hāʾ',    'ه',   'ـه',  'ـهـ', 'هـ',  'h',      'haa2'],
  ['و', 'wāw',    'و',   'ـو',  'ـو',  'و',   'w / ū',  'waw'],
  ['ي', 'yāʾ',    'ي',   'ـي',  'ـيـ', 'يـ',  'y / ī',  'yaa'],
];

const HARAKAT = [
  { id: 'haraka_fatha',  glyph: 'بَ', name: 'Fatḥa',  fr: 'voyelle « a » brève',     example: 'بَ → ba' },
  { id: 'haraka_kasra',  glyph: 'بِ', name: 'Kasra',  fr: 'voyelle « i » brève',     example: 'بِ → bi' },
  { id: 'haraka_damma',  glyph: 'بُ', name: 'Ḍamma',  fr: 'voyelle « u / ou » brève', example: 'بُ → bu' },
  { id: 'haraka_sukun',  glyph: 'بْ', name: 'Sukūn',  fr: 'absence de voyelle (silencieux)', example: 'بْ → b' },
  { id: 'haraka_shadda', glyph: 'بّ', name: 'Shadda', fr: 'gémination — la consonne est doublée', example: 'بّ → bb' },
];

const LONG_VOWELS = [
  { id: 'long_alif', glyph: 'بَا', name: 'Madd alif',   fr: 'voyelle longue « ā » : fatḥa + alif', example: 'بَا → bā' },
  { id: 'long_yaa',  glyph: 'بِي', name: 'Madd yāʾ',    fr: 'voyelle longue « ī » : kasra + yāʾ',  example: 'بِي → bī' },
  { id: 'long_waw',  glyph: 'بُو', name: 'Madd wāw',    fr: 'voyelle longue « ū » : ḍamma + wāw',  example: 'بُو → bū' },
];

const TANWIN = [
  { id: 'tanwin_fath', glyph: 'بً',  name: 'Tanwīn fatḥ',  fr: 'finale « an »',  example: 'بً → ban' },
  { id: 'tanwin_kasr', glyph: 'بٍ',  name: 'Tanwīn kasr',  fr: 'finale « in »',  example: 'بٍ → bin' },
  { id: 'tanwin_damm', glyph: 'بٌ',  name: 'Tanwīn ḍamm',  fr: 'finale « un »',  example: 'بٌ → bun' },
];

const Section = ({ title, subtitle, children, testid }) => (
  <GlassCard className="space-y-4" data-testid={testid}>
    <div>
      <h2 className="text-xl sm:text-2xl font-serif text-text-primary">{title}</h2>
      {subtitle && <p className="text-sm text-text-muted mt-1">{subtitle}</p>}
    </div>
    {children}
  </GlassCard>
);

const LetterCard = ({ row, playingId, onPlay }) => {
  const [letter, name, isolated, finalForm, medial, initial, translit, audioId] = row;
  return (
    <div
      className="rounded-lg border border-border bg-surface-elevated/40 p-4 hover:border-primary/40 transition-colors"
      data-testid={`letter-${name}`}
    >
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <span className="font-arabic text-4xl text-primary leading-none" dir="rtl">{letter}</span>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">{name}</Badge>
          <PlayButton id={audioId} playingId={playingId} onClick={onPlay}
                      label={`Écouter ${name}`} />
        </div>
      </div>
      <p className="text-xs text-text-muted mb-3">/{translit}/</p>
      <div className="grid grid-cols-4 gap-1 font-arabic text-2xl text-text-primary text-center" dir="rtl">
        <div title="Initiale">{initial !== '—' ? initial : '·'}</div>
        <div title="Médiane">{medial !== '—' ? medial : '·'}</div>
        <div title="Finale">{finalForm !== '—' ? finalForm : '·'}</div>
        <div title="Isolée">{isolated !== '—' ? isolated : '·'}</div>
      </div>
      <div className="grid grid-cols-4 gap-1 text-[10px] text-text-muted text-center mt-1">
        <div>Init.</div><div>Méd.</div><div>Fin.</div><div>Isol.</div>
      </div>
    </div>
  );
};

const SignCard = ({ entry, playingId, onPlay }) => (
  <div
    className="rounded-lg border border-border bg-surface-elevated/40 p-4 text-center"
    data-testid={`sign-${entry.name.toLowerCase()}`}
  >
    <div className="flex items-center justify-center gap-2 mb-1">
      <div className="font-arabic text-4xl text-primary" dir="rtl">{entry.glyph}</div>
      {entry.id && (
        <PlayButton id={entry.id} playingId={playingId} onClick={onPlay}
                    label={`Écouter ${entry.name}`} />
      )}
    </div>
    <div className="text-sm font-serif text-text-primary mb-1">{entry.name}</div>
    <div className="text-xs text-text-muted leading-relaxed">{entry.fr}</div>
    <div className="text-[11px] text-text-secondary mt-2 italic">{entry.example}</div>
  </div>
);

const MODULES = [
  { id: 'journey', label: 'Parcours', icon: 'star' },
  { id: 'alphabet', label: 'Alphabet', count: 28, skill: 'letters' },
  { id: 'harakat',  label: 'Harakāt',   count: 5, skill: 'harakat' },
  { id: 'long',     label: 'Voyelles longues', count: 3, skill: 'syllables' },
  { id: 'tanwin',   label: 'Tanwīn',    count: 3, skill: 'tanwin' },
];

// ---- Pedagogical curriculum: per top-level module, per difficulty ----
const CURRICULUM = {
  alphabet: {
    easy:   { skills: ['letters'],
              lessonIds: ['alphabet-intro', 'alphabet-listen'],
              hint: 'Reconnaître les 28 lettres (visuel + audio)' },
    medium: { skills: ['letters', 'letter_shapes'],
              lessonIds: ['alphabet-similar', 'letter-shapes'],
              skipDifficulty: true,
              hint: 'Lettres similaires, formes initiale/médiane/finale' },
    hard:   { skills: ['quran_words'],
              lessonIds: ['quranic-words', 'words-listen'],
              skipDifficulty: false,
              hint: 'Lire des mots simples du Coran (avec audio)' },
  },
  harakat: {
    easy:   { skills: ['harakat', 'sukun'],
              skipDifficulty: true,
              hint: 'Fatḥa, kasra, ḍamma, sukūn' },
    medium: { skills: ['shadda', 'syllables'],
              lessonIds: ['sukun-shadda', 'syllables-listen'],
              skipDifficulty: false,
              hint: 'Shadda, syllabes longues (audio)' },
    hard:   { skills: ['quran_words', 'syllables'],
              lessonIds: ['quranic-words', 'syllables'],
              skipDifficulty: false,
              hint: 'Mots avec ḥarakāt, syllabes écrites' },
  },
  long: {
    easy:   { skills: ['syllables'],
              lessonIds: ['syllables-listen'],
              skipDifficulty: true,
              hint: 'Écouter les syllabes longues (madd)' },
    medium: { skills: ['syllables', 'quran_words'],
              skipDifficulty: false,
              hint: 'Lire syllabes + mots avec voyelles longues' },
    hard:   { skills: ['quran_words', 'ayah_segments'],
              skipDifficulty: false,
              hint: 'Mots et segments de versets' },
  },
  tanwin: {
    easy:   { skills: ['tanwin'], skipDifficulty: true,
              hint: 'Reconnaître an / in / un' },
    medium: { skills: ['tanwin', 'quran_words'],
              skipDifficulty: false,
              hint: 'Tanwīn + mots simples' },
    hard:   { skills: ['quran_words', 'ayah_segments'],
              skipDifficulty: false,
              hint: 'Segments de versets contenant le tanwīn' },
  },
};

const InnerTabs = ({ lesson, moduleId, skill, practiceHref, children, testidPrefix }) => (
  <Tabs defaultValue="lesson" className="space-y-4" data-testid={`${testidPrefix}-inner-tabs`}>
    <TabsList className="bg-surface border border-border">
      <TabsTrigger value="lesson" data-testid={`${testidPrefix}-tab-lesson`}>
        Leçon
      </TabsTrigger>
      <TabsTrigger value="exercises" data-testid={`${testidPrefix}-tab-exercises`}>
        Exercices
      </TabsTrigger>
      <TabsTrigger value="results" data-testid={`${testidPrefix}-tab-results`}>
        Mes résultats
      </TabsTrigger>
    </TabsList>
    <TabsContent value="lesson">{lesson}</TabsContent>
    <TabsContent value="exercises" className="space-y-4">
      {children /* optional extra content */}
      <ExercisesHub
        section="reading"
        skill={skill}
        levels={CURRICULUM[moduleId]}
        title="Séries d'exercices"
        testidPrefix={`${testidPrefix}-hub`}
      />
    </TabsContent>
    <TabsContent value="results">
      <SkillResultsPanel
        section="reading"
        skill={skill}
        practiceHref={practiceHref}
        testidPrefix={`${testidPrefix}-results`}
      />
    </TabsContent>
  </Tabs>
);

export const ApprendreALire = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [active, setActive] = useState('journey');
  const { preferences, setLectureVoice } = usePreferences();
  const voice = preferences.lectureVoice || 'male';
  const { play, playingId } = usePlayer(voice);

  // Journey runner state — when set, replaces the journey list with a player.
  const [running, setRunning] = useState(null);  // {competency, difficulty}
  const [journeyKey, setJourneyKey] = useState(0); // bump to refresh after series

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="lecture-page">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/cours')}
        className="text-text-muted hover:text-text-primary mb-4"
        data-testid="lecture-back-to-cours"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('cours.back_to_cours')}
      </Button>

      <SectionHeader
        overline={t('lecture.overline')}
        title={t('lecture.title')}
        subtitle={t('lecture.subtitle')}
      />

      {/* Voice selector */}
      <div
        className="mt-4 flex items-center justify-between flex-wrap gap-3"
        data-testid="lecture-voice-selector"
      >
        <div className="text-xs uppercase tracking-wider text-text-muted">
          {t('lecture.voice_label')}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 p-1 rounded-md bg-surface-elevated/60 border border-border">
            {[
              { id: 'male',   label: t('lecture.voice_male') },
              { id: 'female', label: t('lecture.voice_female') },
              { id: 'user',   label: t('lecture.voice_user') },
            ].map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setLectureVoice(v.id)}
                aria-pressed={voice === v.id}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  voice === v.id
                    ? 'bg-primary/20 text-primary'
                    : 'text-text-muted hover:text-text-primary'
                }`}
                data-testid={`lecture-voice-${v.id}`}
              >
                {v.label}
              </button>
            ))}
          </div>
          {voice === 'user' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/cours/lecture/upload')}
              className="text-xs"
              data-testid="lecture-go-upload"
            >
              {t('lecture.upload_my_voice')}
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-6 mb-6" role="tablist" data-testid="lecture-tabs">
        {MODULES.map((m) => (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={active === m.id}
            onClick={() => setActive(m.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm transition-colors ${
              active === m.id
                ? 'border-primary/60 bg-primary/15 text-primary'
                : 'border-border bg-surface-elevated/60 text-text-muted hover:text-text-primary'
            }`}
            data-testid={`lecture-tab-${m.id}`}
          >
            {m.label}
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{m.count}</Badge>
          </button>
        ))}
      </div>

      <motion.div
        key={active}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {active === 'journey' && (
          <div data-testid="lecture-journey">
            {running ? (
              <JourneyCompetencyRunner
                competency={running.competency}
                difficulty={running.difficulty}
                onExit={() => {
                  setRunning(null);
                  setJourneyKey((k) => k + 1);
                }}
                testidPrefix="lecture-journey-runner"
              />
            ) : (
              <CurriculumJourney
                key={journeyKey}
                curriculumId="curr_reading_beginner"
                onStartCompetency={(c, d) => setRunning({ competency: c, difficulty: d })}
                testidPrefix="lecture-journey"
              />
            )}
          </div>
        )}

        {active === 'alphabet' && (
          <InnerTabs
            moduleId="alphabet"
            skill="letters"
            testidPrefix="lecture-alphabet"
            lesson={
              <Section
                title={t('lecture.alphabet_title')}
                subtitle={t('lecture.alphabet_subtitle')}
                testid="alphabet-section"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {ALPHABET.map((row) => (
                    <LetterCard key={row[1]} row={row} playingId={playingId} onPlay={play} />
                  ))}
                </div>
              </Section>
            }
          />
        )}

        {active === 'harakat' && (
          <InnerTabs
            moduleId="harakat"
            skill="harakat"
            testidPrefix="lecture-harakat"
            lesson={
              <Section
                title={t('lecture.harakat_title')}
                subtitle={t('lecture.harakat_subtitle')}
                testid="harakat-section"
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {HARAKAT.map((h) => <SignCard key={h.name} entry={h} playingId={playingId} onPlay={play} />)}
                </div>
              </Section>
            }
          />
        )}

        {active === 'long' && (
          <InnerTabs
            moduleId="long"
            skill="syllables"
            testidPrefix="lecture-long"
            lesson={
              <Section
                title={t('lecture.long_title')}
                subtitle={t('lecture.long_subtitle')}
                testid="long-section"
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {LONG_VOWELS.map((h) => <SignCard key={h.name} entry={h} playingId={playingId} onPlay={play} />)}
                </div>
                <p className="text-xs text-text-muted mt-3">
                  {t('lecture.long_hint')}
                </p>
              </Section>
            }
          />
        )}

        {active === 'tanwin' && (
          <InnerTabs
            moduleId="tanwin"
            skill="tanwin"
            testidPrefix="lecture-tanwin"
            lesson={
              <Section
                title={t('lecture.tanwin_title')}
                subtitle={t('lecture.tanwin_subtitle')}
                testid="tanwin-section"
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {TANWIN.map((h) => <SignCard key={h.name} entry={h} playingId={playingId} onPlay={play} />)}
                </div>
                <p className="text-xs text-text-muted mt-3">
                  {t('lecture.tanwin_hint')}
                </p>
              </Section>
            }
          />
        )}
      </motion.div>

      <GlassCard className="mt-6 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BookOpenText className="w-5 h-5 text-primary" />
          <div>
            <p className="text-sm text-text-primary">{t('lecture.next_step_title')}</p>
            <p className="text-xs text-text-muted">{t('lecture.next_step_subtitle')}</p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/cours/tajwid')}
          variant="outline"
          size="sm"
          data-testid="lecture-go-to-tajwid"
        >
          {t('lecture.go_to_tajwid')}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </GlassCard>
    </div>
  );
};

export default ApprendreALire;
